// Parent-authored assignments — distinct from seed/yearPlanSeed.js, which is
// one fixed hardcoded curriculum. These two functions are the general-
// purpose "define work" path any course can use: a single ad-hoc entry, or
// a series spread across weeks (gap-review item 2: "divide this curriculum
// across N weeks at H hours/week" without hand-authoring every day).
import { addDaysIso, isoWeekdayNumber, weekNumberForDate } from './calendarMath';

// Weekday patterns (1=Mon..5=Fri) for spreading N sessions/week across the
// week rather than clumping them — e.g. 3/week lands Mon/Wed/Fri, not
// Mon/Tue/Wed.
const SPREAD_PATTERNS = {
  1: [1],
  2: [1, 4],
  3: [1, 3, 5],
  4: [1, 2, 4, 5],
  5: [1, 2, 3, 4, 5],
};

function slugify(s) {
  return (
    s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'item'
  );
}

function blank(base, nowIso, source) {
  return {
    ...base,
    confusionFlag: false,
    status: 'not_started',
    reviewedByParent: false,
    source,
    createdAt: nowIso,
    updatedAt: nowIso,
  };
}

// One assignment on one date — the "quick add" path. Always placementMode
// 'fixed' — there's no "which day this week" ambiguity for a single dated entry.
export function buildManualAssignment({ studentEmail, courseId, date, block, title, subLabel, resourceLink, estMinutes, isFloor, needsReview, needsParent, deliveryMode, calendar }) {
  const nowIso = new Date().toISOString();
  const weekNumber = calendar?.weeks ? weekNumberForDate(calendar.weeks, date) : 0;
  return blank(
    {
      id: `manual__${date}__${slugify(`${courseId}-${title}`)}-${Date.now()}`,
      studentEmail,
      courseId,
      date,
      weekNumber,
      dayOfWeek: isoWeekdayNumber(date),
      block,
      order: 700,
      title,
      subLabel: subLabel || undefined,
      resourceLink: resourceLink || undefined,
      estMinutes,
      isFloor: !!isFloor,
      placementMode: 'fixed',
      needsReview: needsReview ?? true,
      needsParent: !!needsParent,
      deliveryMode: deliveryMode || undefined,
    },
    nowIso,
    'manual'
  );
}

// Spreads one course's work across `weeksCount` weeks starting at
// `startWeekNumber` (1-indexed into calendar.weeks), `sessionsPerWeek` times
// a week, splitting `hoursPerWeek` evenly across those sessions.
//
// Two placement modes (a true conflict with the real year plan, resolved by
// keeping both — see the school-module-v2 plan):
//   'fixed' (default before the real-year-plan work; still useful for
//       anything with a real deadline) — pre-assigns each session to a
//       specific weekday via SPREAD_PATTERNS, same as before.
//   'student-placed' (the weekly-board model, e.g. Composition/Cursive/
//       Digital Savvy) — emits `sessionsPerWeek` tiles per week with
//       date/dayOfWeek/order left null; the student places each one via
//       the week board (WeekBoard.jsx, api.js's placeAssignment).
// Deterministic ids either way — re-running with the same inputs upserts
// rather than duplicating.
export function generateSeriesAssignments({
  studentEmail, courseId, title, subLabel, resourceLink, block,
  weeksCount, startWeekNumber, sessionsPerWeek, hoursPerWeek, isFloor, needsReview,
  needsParent, deliveryMode, placementMode = 'student-placed', calendar,
}) {
  if (!calendar?.weeks?.length) return [];
  const estMinutes = Math.round((hoursPerWeek * 60) / sessionsPerWeek);
  const nowIso = new Date().toISOString();
  const out = [];
  const commonFields = {
    studentEmail, courseId, block, title,
    subLabel: subLabel || undefined,
    resourceLink: resourceLink || undefined,
    estMinutes, isFloor: !!isFloor, placementMode,
    needsReview: needsReview ?? true,
    needsParent: !!needsParent,
    deliveryMode: deliveryMode || undefined,
  };

  for (let w = 0; w < weeksCount; w++) {
    const weekNumber = startWeekNumber + w;
    const monday = calendar.weeks[weekNumber - 1];
    if (!monday) break; // ran past the end of the school year

    if (placementMode === 'student-placed') {
      for (let n = 1; n <= sessionsPerWeek; n++) {
        out.push(
          blank(
            { id: `series__${weekNumber}__${slugify(`${courseId}-${title}`)}__${n}`,
              ...commonFields, weekNumber, date: null, dayOfWeek: null, order: null },
            nowIso,
            'manual'
          )
        );
      }
      continue;
    }

    const pattern = SPREAD_PATTERNS[Math.min(Math.max(sessionsPerWeek, 1), 5)];
    for (const dow of pattern) {
      const date = addDaysIso(monday, dow - 1);
      if (calendar.holidays?.[date]) continue;
      out.push(
        blank(
          { id: `series__${courseId}__${date}__${slugify(title)}`,
            ...commonFields, weekNumber, date, dayOfWeek: dow, order: 800 },
          nowIso,
          'manual'
        )
      );
    }
  }
  return out;
}

// Soft sequencing hints for weekly-board placement — never block a
// placement, only surface a one-line nudge, per her own "every number is a
// cap, not a target" design elsewhere. `existingToday` is the list of
// already-placed assignments (any placementMode) for the day being
// considered; `item` is the tile about to be placed there.
// "Two heavy blocks before lunch — math, literature, science or
// composition" (her exact wording). Literature/composition are block 'la';
// science-text/science-lab share courseId 'science' but live in block
// 'afternoon' alongside non-heavy tiles (Digital Savvy, Dr. Bonyfide), so
// heaviness needs a courseId check too, not just block.
const HEAVY_BLOCKS = ['math', 'la'];
function isHeavy(a) {
  return HEAVY_BLOCKS.includes(a.block) || a.courseId === 'science';
}

export function checkSequencingRules({ dayOfWeek, existingToday, item, isAfterSwim }) {
  const warnings = [];
  const heavyCount = existingToday.filter(isHeavy).length;

  if (isHeavy(item) && heavyCount >= 2) {
    warnings.push('Two heavy blocks are already on this day — afternoons are usually for finishing, not starting.');
  }
  if (isAfterSwim && dayOfWeek !== 5) {
    warnings.push('Nothing new usually starts after the pool — consider a lighter tile here.');
  }
  if (dayOfWeek === 5 && item.courseId === 'history') {
    warnings.push("Friday carries the double history lesson — that's expected, not a conflict.");
  }
  if (item.avoidDays?.includes(dayOfWeek)) {
    warnings.push("This one usually skips this day — the tutor (or another fixed commitment) already has it.");
  }
  return warnings;
}
